import {
    AcceptFamilyEventPayload,
    Family, FamilyCreateDto,
    FamilyUpdateDtoInterface, PendingFamily, PotentialDuplicateActionConstants
} from '@/families/models/family';
import { getModule } from 'vuex-module-decorators';
import { CentersStore } from '@/organizations/locations/stores/centers-store';
import { FamilyMapper } from '@/families/mappers/family-mapper';
import { CrmTypeList, CrmTypeOption } from '@/crm-types/models/crm-type';
import { CrmTypesStore } from '@/crm-types/store/crm-types-store';
import { Child, ChildCreateDtoInterface, ChildUpdateDtoInterface } from '@/families/models/child';
import {
    buildFamilyEntries,
    generateDifferencesChildrenMap,
    generateDifferencesFamilyMap, getCenterNames, getMergeActionsMap,
    groupChildrenByFullName
} from '@/families/potential-duplicate-utils';
import { StatusChangeInterface } from '@/families/models/status';
import { ChangeStatus } from '@/families/change-status';
import { FamiliesRepository } from '@/families/repositories/families-repository';
import { ChildrenRepository } from '@/families/repositories/children-repository';
import { StatusChangeRepository } from '@/families/repositories/status-change-repository';
import { StatusChangesStore } from '@/families/store/status-changes-store';
import { PendingFamilyService } from '@/families/pending-family-service';
import { EnrollmentSchedule, EnrollmentUpdateDtoInterface } from '@/families/models/enrollment';
import { FeaturesStore } from '@/features/features-store';
import { FeatureConstants } from '@/features/feature-constants';
import { EnrollmentsRepository } from '@/families/repositories/enrollments-repository';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { StatusesStore } from '@/families/store/statuses-store';
import { AuthStore } from '@/store/auth-store';
import store from '@/store';

export interface FromAddFamilyPayload {
    familyDto: FamilyUpdateDtoInterface | FamilyCreateDto;
    pendingFamily: PendingFamily | null;
    childrenStatusUpdates: Array<StatusChangeInterface | null>;
    classrooms: Array<number | null>;
    schedules: Array<EnrollmentSchedule | undefined>;
}

export interface ChildEntry {
    childDto: ChildUpdateDtoInterface | ChildCreateDtoInterface;
    child: Child | null;
    family: Family | PendingFamily | null;
    availableStatuses: Array<number>;
    statusUpdates: StatusChangeInterface | null;
    classroom?: number;
    schedule?: EnrollmentSchedule;
}

export interface FamilyEntry {
    familyDto: FamilyUpdateDtoInterface | FamilyCreateDto;
    family: Family | PendingFamily | null;
    statusUpdates: StatusChangeInterface | null;
}

export interface PotentialDuplicateActionOption {
    text: string;
    value: string;
}

export interface FieldOverwrite {
    fieldPath: string;
    oldValue: any;
    mergedValue: any;
}

export interface MergeLayer {
    sourceId: number;
    familyOverwrites: Array<FieldOverwrite>;
    childrenOverwrites: Record<string, Array<FieldOverwrite>>;
}

export default class PotentialDuplicateService {
    private featuresStore = getModule(FeaturesStore);
    private centerStore = getModule(CentersStore);
    private crmTypesStore = getModule(CrmTypesStore);
    private centerNames: Array<string> = [];
    private currentFamilyEntries: Array<FamilyEntry> = [];
    private originalFamilyEntries: Array<FamilyEntry> = [];
    private familyMapper = new FamilyMapper();
    private inquiryTypes: Array<CrmTypeOption> = [];
    private sourceTypes: Array<CrmTypeOption> = [];
    private currentChildrenGroupedByName: Record<string, Array<ChildEntry>> = {};
    private originalChildrenGroupedByName: Record<string, Array<ChildEntry>> = {};
    private familyDifferencesRecord: Record<string, boolean> = {};
    private childrenDifferencesArray: Array<{ name: string; differences: Record<string, boolean> }> = [];
    private statusIdsForChildlessFamilies: Array<number> = [];
    private changeStatusUtil = new ChangeStatus();
    private familiesRepository = new FamiliesRepository();
    private childrenRepository = new ChildrenRepository();
    private statusChangeRepository = new StatusChangeRepository();
    private enrollmentsRepository = new EnrollmentsRepository()
    private statusChangesStore = getModule(StatusChangesStore);
    private pendingFamilyService = new PendingFamilyService();
    private addFamilyPayload: FromAddFamilyPayload | null = null;
    private statusStores = getModule(StatusesStore);
    private authStore = getModule(AuthStore, store);

    get isClassroomsFeatureEnabled(): boolean {
        return this.featuresStore.isFeatureEnabled(FeatureConstants.CLASSROOMS);
    }

    get inquiryTypesOptions() {
        return this.inquiryTypes;
    }

    get sourceTypesOptions() {
        return this.sourceTypes;
    }

    get statusesForChildlessFamilies() {
        return this.statusIdsForChildlessFamilies;
    }

    get centerRows() {
        return this.centerNames;
    }

    get familyEntries() {
        return this.currentFamilyEntries;
    }

    get childrenGrouped() {
        return this.currentChildrenGroupedByName;
    }

    get familyDifferences() {
        return this.familyDifferencesRecord;
    }

    get childrenDifferences() {
        return this.childrenDifferencesArray;
    }

    public updateFamilyEntries(value: Array<FamilyEntry>) {
        this.currentFamilyEntries = value;
    }

    public updateChildrenGroupedByName(value: Record<string, Array<ChildEntry>>) {
        this.currentChildrenGroupedByName = value;
    }

    // Optimized update methods for specific fields to avoid full object replacements
    public updateFamilyField(familyIndex: number, fieldPath: string, value: any) {
        if (!this.currentFamilyEntries[familyIndex]) return;

        const keys = fieldPath.split('.');
        let current: any = this.currentFamilyEntries[familyIndex].familyDto;

        // Navigate to the nested property
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
        }

        // Update only the specific field
        current[keys[keys.length - 1]] = value;
    }

    public updateChildField(childName: string, childIndex: number, fieldPath: string, value: any) {
        const childEntry = this.currentChildrenGroupedByName[childName]?.[childIndex];
        if (!childEntry) return;

        const keys = fieldPath.split('.');
        let current: any = childEntry.childDto;

        // Navigate to the nested property
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
        }

        // Update only the specific field
        current[keys[keys.length - 1]] = value;
    }

    async init(families: Array<Family | PendingFamily | null>, fromAddFamilyPayload: FromAddFamilyPayload | null = null) {
        // Reset state efficiently
        this.currentFamilyEntries = [];
        this.centerNames = [];
        this.currentChildrenGroupedByName = {};
        this.familyDifferencesRecord = {};
        this.childrenDifferencesArray = [];
        this.addFamilyPayload = fromAddFamilyPayload;

        // Parallel initialization for better performance
        await Promise.all([
            this.featuresStore.init(),
            this.statusStores.init(),
            this.centerStore.initAllSimpleCenters()
        ]);

        // Build family entries first
        this.currentFamilyEntries = buildFamilyEntries(families, fromAddFamilyPayload);

        // Early return if no families to process
        if (this.currentFamilyEntries.length === 0) {
            return;
        }

        // Extract data once to avoid repeated map operations
        const currentFamiliesDto = this.currentFamilyEntries.map(familyEntry => familyEntry.familyDto);
        const currentFamiliesStatusUpdates = this.currentFamilyEntries.map(familyEntry => familyEntry.statusUpdates);

        // Process children grouping
        const childrenGroupedMap = groupChildrenByFullName(currentFamiliesDto, families, fromAddFamilyPayload);
        this.currentChildrenGroupedByName = Object.fromEntries(childrenGroupedMap.entries());

        // Clone the original data for comparison purposes
        this.originalFamilyEntries = cloneDeep(this.currentFamilyEntries);
        this.originalChildrenGroupedByName = cloneDeep(this.currentChildrenGroupedByName);

        // Build center names and differences synchronously
        this.centerNames = getCenterNames(this.currentFamilyEntries, this.centerStore.storedAllSimpleCenters);

        // Build differences
        this.familyDifferencesRecord = generateDifferencesFamilyMap(currentFamiliesDto, currentFamiliesStatusUpdates);

        // Build children differences more efficiently
        this.childrenDifferencesArray = Object.keys(this.currentChildrenGroupedByName).map(name => {
            const childEntries = this.currentChildrenGroupedByName[name];
            return {
                name,
                differences: generateDifferencesChildrenMap(
                    childEntries.map(childEntry => childEntry.childDto),
                    childEntries.map(childEntry => childEntry.statusUpdates)
                )
            };
        });
    }

    async setupSelectListOptions() {
        const familyInquiryPromise = this.crmTypesStore.initList(CrmTypeList.FAMILY_INQUIRY);
        const familySourcePromise = this.crmTypesStore.initList(CrmTypeList.FAMILY_SOURCE);
        await Promise.all([familyInquiryPromise, familySourcePromise]);

        this.inquiryTypes = [];
        this.sourceTypes = [];
        this.statusIdsForChildlessFamilies = [];

        const inquiryTypesSet: Set <number> = new Set();
        const sourceTypesSet: Set <number> = new Set();
        const statusIdsForChildlessFamiliesSet: Set<number> = new Set();
        for (const familyEntry of this.familyEntries) {
            if (familyEntry.familyDto.inquiry_type) {
                inquiryTypesSet.add(familyEntry.familyDto.inquiry_type);
            }
            if (familyEntry.familyDto.source_type) {
                sourceTypesSet.add(familyEntry.familyDto.source_type);
            }
            if (familyEntry.familyDto.children.length === 0) {
                let currentStatus = 1; // Default
                if ('status' in familyEntry.familyDto && familyEntry.familyDto.status) {
                    currentStatus = familyEntry.familyDto.status;
                } else if (familyEntry.familyDto.primary_guardian.status) {
                    currentStatus = familyEntry.familyDto.primary_guardian.status;
                }
                statusIdsForChildlessFamiliesSet.add(currentStatus);
            }
        }
        this.inquiryTypes = this.crmTypesStore.listOptions(CrmTypeList.FAMILY_INQUIRY).filter(option => inquiryTypesSet.has(option.id));
        this.sourceTypes = this.crmTypesStore.listOptions(CrmTypeList.FAMILY_SOURCE).filter(option => sourceTypesSet.has(option.id));
        this.statusIdsForChildlessFamilies = Array.from(statusIdsForChildlessFamiliesSet);
    }

    private async updateEnrollment(centerId: number, newChild: Child, childEntry: ChildEntry) {
        const enrollmentUpdate: EnrollmentUpdateDtoInterface = {
            id: newChild.enrollments[0].id,
            center_id: centerId,
            child_id: newChild.id
        };
        if (this.isClassroomsFeatureEnabled && childEntry.classroom) {
            enrollmentUpdate.classroom_id = childEntry.classroom;
        }
        if (childEntry.schedule) {
            enrollmentUpdate.schedule = childEntry.schedule;
        }
        await this.enrollmentsRepository.update(enrollmentUpdate);
    }

    // Helper method to check if pending family should be accepted
    private async acceptPendingFamilyIfNeeded(
        familyEntry: FamilyEntry,
        actionType: string,
        familyId: number,
        mergeActionsMap: Map<number, Set<number>>,
        familyStatusMap: Map<number, number | null>
    ): Promise<void> {
        const isLinkAction = actionType === PotentialDuplicateActionConstants.LINK;
        const isSaveAction = actionType === PotentialDuplicateActionConstants.SAVE;

        if (familyEntry.family && familyEntry.family.status === null && (isLinkAction || isSaveAction)) {
            let doNotRunWorkflows = false;

            if (isSaveAction) {
                const sourceFamilyIds = mergeActionsMap.get(familyId);
                if (sourceFamilyIds) {
                    for (const sourceId of sourceFamilyIds) {
                        const sourceStatus = familyStatusMap.get(sourceId);
                        if (sourceStatus && sourceStatus > 0) {
                            doNotRunWorkflows = true;
                            break;
                        }
                    }
                }
            }

            const payload: AcceptFamilyEventPayload = { doNotRunWorkflows };
            await this.pendingFamilyService.acceptFamily(familyId, payload);
        }
    }

    // Helper method to update family status if needed
    private async updateFamilyStatusIfNeeded(familyEntry: FamilyEntry, familyId: number): Promise<void> {
        if (familyEntry.statusUpdates && familyEntry.statusUpdates.family_id > 0) {
            // Find the original family entry to compare against
            const originalEntry = this.originalFamilyEntries.find(entry => entry.familyDto.id === familyId);
            const originalStatusUpdates = originalEntry?.statusUpdates;
            const currentStatusUpdates = familyEntry.statusUpdates;

            // Only update if the entire statusUpdates object has changed from the original
            if (originalStatusUpdates && !isEqual(originalStatusUpdates, currentStatusUpdates)) {
                await this.statusChangeRepository.changeStatus(familyEntry.statusUpdates, true);
                this.statusChangesStore.clear(familyId);
            }
        }
    }

    // Helper method to update child status if needed
    private async updateChildStatusIfNeeded(childEntry: ChildEntry): Promise<void> {
        if (
            childEntry &&
            childEntry.childDto.id &&
            childEntry.statusUpdates &&
            childEntry.statusUpdates.status > 0 &&
            childEntry.statusUpdates.child_id &&
            childEntry.statusUpdates.child_id > 0
        ) {
            const currentChildId = childEntry.childDto.id;

            // Find the original child entry to compare against
            let originalStatusUpdates: StatusChangeInterface | null | undefined;
            for (const [, entries] of Object.entries(this.originalChildrenGroupedByName)) {
                const originalEntry = entries.find(entry => entry.childDto.id === currentChildId);
                if (originalEntry) {
                    originalStatusUpdates = originalEntry.statusUpdates;
                    break;
                }
            }

            const currentStatusUpdates = childEntry.statusUpdates;

            // Only update if the entire statusUpdates object has changed from the original
            if (originalStatusUpdates && !isEqual(originalStatusUpdates, currentStatusUpdates)) {
                await this.statusChangeRepository.changeStatus(childEntry.statusUpdates, true);
                this.statusChangesStore.clear(currentChildId);
            }
        }
    }

    // Helper method to process child updates or creation
    private async processChildUpdateOrCreate(
        childEntry: ChildEntry,
        familyId: number,
        centerId: number | undefined
    ): Promise<void> {
        const childId = childEntry.childDto.id;
        if (!childId) return;

        if (childId > 0) {
            // Check if center user is trying to update a child from a different center
            const isCenterUser = this.authStore.isCenterStaff;
            const userCenterId = this.authStore.centerId;
            const familyCenterId = centerId;
            const isDifferentCenter = isCenterUser && userCenterId && familyCenterId && userCenterId !== familyCenterId;

            // Only update if not a center user trying to update a different center's child
            if (!isDifferentCenter) {
                await this.childrenRepository.update(familyId, childEntry.childDto as ChildUpdateDtoInterface);
            }
        } else if (
            childEntry.childDto.first_name &&
            childEntry.childDto.last_name &&
            childEntry.statusUpdates &&
            childEntry.statusUpdates.status > 0
        ) {
            const newChildDto = childEntry.childDto as ChildCreateDtoInterface;
            delete newChildDto.id;
            newChildDto.status = 1; // Set New Family status.
            const newChild = (await this.childrenRepository.create(familyId, newChildDto))[0];
            childEntry.statusUpdates.child_id = newChild.id;

            if (centerId && (childEntry.schedule || childEntry.classroom)) {
                await this.updateEnrollment(centerId, newChild, childEntry);
            }
        }
    }

    // Helper method to create a new child for primary family
    private async createPrimaryFamilyChild(
        childEntry: ChildEntry,
        primaryFamilyId: number,
        centerId: number | undefined
    ): Promise<void> {
        const childDto = childEntry.childDto;
        const childId = childDto.id;

        if (childId && childDto.first_name && childDto.last_name && childEntry.statusUpdates && childEntry.statusUpdates.status > 0) {
            const newChildDto = { ...childDto } as ChildCreateDtoInterface;
            delete newChildDto.id;
            newChildDto.status = 1; // New Family status.
            const newChild = (await this.childrenRepository.create(primaryFamilyId, newChildDto))[0];
            childEntry.statusUpdates.child_id = newChild.id;
            childEntry.statusUpdates.family_id = primaryFamilyId;

            if (centerId && (childEntry.schedule || childEntry.classroom)) {
                await this.updateEnrollment(centerId, newChild, childEntry);
            }
        }
    }

    // Helper method to build family status map
    private buildFamilyStatusMap(): Map<number, number | null> {
        const familyStatusMap = new Map<number, number | null>();
        for (const entry of this.currentFamilyEntries) {
            if (entry.family && entry.family.id) {
                familyStatusMap.set(entry.family.id, entry.family.status?.id ?? null);
            }
        }
        return familyStatusMap;
    }

    async save(selectedActions: Record<number, PotentialDuplicateActionOption | null>): Promise<number | null> {
        let result = null;
        if (this.addFamilyPayload && this.addFamilyPayload.pendingFamily === null) {
            // If there is an add family payload and the pending family is null, meaning a new family will be created and we need to return the new family id
            result = await this.processSavingWithAddFamilyModalPayload(selectedActions);
        } else {
            await this.processSaving(selectedActions);
        }
        return result;
    }

    async processSaving(selectedActions: Record<number, PotentialDuplicateActionOption | null>) {
        // These two operations must be sequential as post actions depend on family updates
        await this.processFamilyAndChildrenUpdates(selectedActions);
        await this.processFamilyPostActions(selectedActions);
    }

    private async processFamilyAndChildrenUpdates(selectedActions: Record<number, PotentialDuplicateActionOption | null>) {
        const mergeActionsMap = getMergeActionsMap(selectedActions);
        const familyStatusMap = this.buildFamilyStatusMap();

        // Step 1: Process all pending family accepts first (in parallel)
        const acceptPromises: Array<Promise<any>> = [];
        for (const familyEntry of this.currentFamilyEntries) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            const [actionType] = selectedAction.value.split('_');

            // Collect accept promises
            acceptPromises.push(
                this.acceptPendingFamilyIfNeeded(
                    familyEntry,
                    actionType,
                    currentFamilyId,
                    mergeActionsMap,
                    familyStatusMap
                )
            );
        }
        await Promise.all(acceptPromises);

        // Step 2: Process all family updates in parallel
        const familyUpdatePromises: Array<Promise<any>> = [];
        for (const familyEntry of this.currentFamilyEntries) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            // Check if center user is trying to update a family from a different center
            const isCenterUser = this.authStore.isCenterStaff;
            const userCenterId = this.authStore.centerId;
            const familyCenterId = familyEntry.familyDto.primary_guardian?.center_id;
            const isDifferentCenter = isCenterUser && userCenterId && familyCenterId && userCenterId !== familyCenterId;

            // Only update if not a center user trying to update a different center's family
            if (!isDifferentCenter) {
                familyUpdatePromises.push(
                    this.familiesRepository.update(familyEntry.familyDto as FamilyUpdateDtoInterface, true)
                );
            }
        }
        await Promise.all(familyUpdatePromises);

        // Step 3: Process all child updates in parallel
        const childUpdatePromises: Array<Promise<any>> = [];
        for (const [i, familyEntry] of this.currentFamilyEntries.entries()) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            // Process children for this family
            for (const [, childEntries] of Object.entries(this.currentChildrenGroupedByName)) {
                const currentChildEntry = childEntries[i];
                if (!currentChildEntry) continue;

                const centerId = familyEntry.familyDto.primary_guardian.center_id;
                childUpdatePromises.push(
                    this.processChildUpdateOrCreate(
                        currentChildEntry,
                        currentFamilyId,
                        centerId || undefined
                    )
                );
            }
        }
        await Promise.all(childUpdatePromises);
    }

    private async processFamilyPostActions(selectedActions: Record<number, PotentialDuplicateActionOption | null>) {
        // Step 1: Process reject/merge actions (must be sequential for order)
        for (const familyEntry of this.currentFamilyEntries) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            const [actionType, targetFamilyString] = selectedAction.value.split('_');
            const targetFamilyId = parseInt(targetFamilyString, 10);

            // Handle reject or merge actions
            if (actionType === PotentialDuplicateActionConstants.REJECT) {
                await this.familiesRepository.rejectFamily(currentFamilyId, {});
            } else if (actionType === PotentialDuplicateActionConstants.MERGE) {
                await this.familiesRepository.rejectFamily(currentFamilyId, { merge_to_family_id: targetFamilyId });
            }
        }

        // Step 2: Process link actions in parallel
        const linkPromises: Array<Promise<any>> = [];
        for (const familyEntry of this.currentFamilyEntries) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            const [actionType, targetFamilyString] = selectedAction.value.split('_');
            const targetFamilyId = parseInt(targetFamilyString, 10);

            if (actionType === PotentialDuplicateActionConstants.LINK) {
                linkPromises.push(
                    this.familiesRepository.linkFamily(targetFamilyId, { family_id: currentFamilyId })
                );
            }
        }
        await Promise.all(linkPromises);

        // Step 3: Process all status updates in parallel
        const statusUpdatePromises: Array<Promise<any>> = [];
        for (const [i, familyEntry] of this.currentFamilyEntries.entries()) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            // Check if family status update is needed
            const originalEntry = this.originalFamilyEntries.find(entry => entry.familyDto.id === currentFamilyId);
            const originalStatusUpdates = originalEntry?.statusUpdates;
            const currentStatusUpdates = familyEntry.statusUpdates;

            if (familyEntry.statusUpdates && familyEntry.statusUpdates.family_id > 0 &&
                originalStatusUpdates && !isEqual(originalStatusUpdates, currentStatusUpdates)) {
                statusUpdatePromises.push(this.updateFamilyStatusIfNeeded(familyEntry, currentFamilyId));
            }

            // Process status changes for child entries
            for (const [, childEntries] of Object.entries(this.currentChildrenGroupedByName)) {
                const currentChildEntry = childEntries[i];
                if (currentChildEntry && this.shouldUpdateChildStatus(currentChildEntry)) {
                    statusUpdatePromises.push(this.updateChildStatusIfNeeded(currentChildEntry));
                }
            }
        }
        await Promise.all(statusUpdatePromises);
    }

    // Helper method to check if child status should be updated
    private shouldUpdateChildStatus(childEntry: ChildEntry): boolean {
        if (!childEntry?.childDto.id || !childEntry.statusUpdates ||
            childEntry.statusUpdates.status <= 0 || !childEntry.statusUpdates.child_id ||
            childEntry.statusUpdates.child_id <= 0) {
            return false;
        }

        // Check against original
        let originalStatusUpdates: StatusChangeInterface | null | undefined;
        for (const [, entries] of Object.entries(this.originalChildrenGroupedByName)) {
            const originalEntry = entries.find(entry => entry.childDto.id === childEntry.childDto.id);
            if (originalEntry) {
                originalStatusUpdates = originalEntry.statusUpdates;
                break;
            }
        }

        return originalStatusUpdates !== undefined &&
               !isEqual(originalStatusUpdates, childEntry.statusUpdates);
    }

    async processSavingWithAddFamilyModalPayload(selectedActions: Record<number, PotentialDuplicateActionOption | null>): Promise<number> {
        // Must be sequential: need primaryFamilyId before processing other updates
        const primaryFamilyId = await this.processPrimaryFamilyAndChildrenCreation(selectedActions);

        // These two can potentially be parallel, but post actions may depend on family updates
        // so keeping them sequential for safety
        await this.processOtherFamilyAndChildrenUpdates(selectedActions);
        await this.processFamilyPostActionsWithPrimaryId(selectedActions, primaryFamilyId);
        return primaryFamilyId;
    }

    private async processPrimaryFamilyAndChildrenCreation(selectedActions: Record<number, PotentialDuplicateActionOption | null>): Promise<number> {
        const primaryEntry = this.currentFamilyEntries[0];
        let primaryFamilyId = primaryEntry.familyDto.id!;
        const primaryAction = selectedActions[primaryFamilyId];
        const isAddingFamily = this.addFamilyPayload && this.addFamilyPayload.pendingFamily === null;

        if (primaryAction && isAddingFamily) {
            const [actionType] = primaryAction.value.split('_');
            if (
                actionType !== PotentialDuplicateActionConstants.REJECT &&
                actionType !== PotentialDuplicateActionConstants.MERGE
            ) {
                const dto = cloneDeep(primaryEntry.familyDto);
                // Remove existing identifiers before creating a new family
                delete dto.id;
                delete dto.children;
                const newFamily = await this.familiesRepository.create(dto as FamilyCreateDto);
                primaryFamilyId = newFamily.id;
                if (primaryEntry.statusUpdates) {
                    primaryEntry.statusUpdates.family_id = newFamily.id;
                }
            }

            // Process primary family children in parallel
            const centerId = primaryEntry.familyDto.primary_guardian.center_id;
            const childCreationPromises: Array<Promise<any>> = [];
            for (const [, childEntries] of Object.entries(this.currentChildrenGroupedByName)) {
                const primaryChildEntry = childEntries[0];
                if (!primaryChildEntry) continue;

                childCreationPromises.push(
                    this.createPrimaryFamilyChild(primaryChildEntry, primaryFamilyId, centerId || undefined)
                );
            }
            await Promise.all(childCreationPromises);
        }

        return primaryFamilyId;
    }

    private async processOtherFamilyAndChildrenUpdates(selectedActions: Record<number, PotentialDuplicateActionOption | null>): Promise<void> {
        const mergeActionsMap = getMergeActionsMap(selectedActions);
        const familyStatusMap = this.buildFamilyStatusMap();

        // Step 1: Process pending accepts for non-primary families (in parallel)
        const acceptPromises: Array<Promise<any>> = [];
        for (let i = 1; i < this.currentFamilyEntries.length; i++) {
            const familyEntry = this.currentFamilyEntries[i];
            const familyId = familyEntry.familyDto.id!;
            const action = selectedActions[familyId];

            if (action) {
                const [actionType] = action.value.split('_');

                // Collect accept promises
                acceptPromises.push(
                    this.acceptPendingFamilyIfNeeded(
                        familyEntry,
                        actionType,
                        familyId,
                        mergeActionsMap,
                        familyStatusMap
                    )
                );
            }
        }
        await Promise.all(acceptPromises);

        // Step 2: Process all family updates in parallel
        const updatePromises: Array<Promise<any>> = [];

        for (let i = 1; i < this.currentFamilyEntries.length; i++) {
            const familyEntry = this.currentFamilyEntries[i];
            const familyId = familyEntry.familyDto.id!;
            const action = selectedActions[familyId];

            if (action) {
                // Check if center user is trying to update a family from a different center
                const isCenterUser = this.authStore.isCenterStaff;
                const userCenterId = this.authStore.centerId;
                const familyCenterId = familyEntry.familyDto.primary_guardian?.center_id;
                const isDifferentCenter = isCenterUser && userCenterId && familyCenterId && userCenterId !== familyCenterId;

                // Only update if not a center user trying to update a different center's family
                if (!isDifferentCenter) {
                    updatePromises.push(
                        this.familiesRepository.update(familyEntry.familyDto as FamilyUpdateDtoInterface, true)
                    );
                }
            }

            // Process children for this family entry
            for (const [, childEntries] of Object.entries(this.currentChildrenGroupedByName)) {
                const childEntry = childEntries[i];
                if (!childEntry) continue;

                // Note: processChildUpdateOrCreate will handle both update and create cases
                updatePromises.push(
                    this.processChildUpdateOrCreate(childEntry, familyId, undefined)
                );
            }
        }

        await Promise.all(updatePromises);
    }

    private async processFamilyPostActionsWithPrimaryId(
        selectedActions: Record<number, PotentialDuplicateActionOption | null>,
        primaryFamilyId: number
    ): Promise<void> {
        // Step 1: Process reject/merge actions (must be sequential)
        for (const [i, familyEntry] of this.currentFamilyEntries.entries()) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            const [actionType, targetFamilyString] = selectedAction.value.split('_');
            const targetFamilyId = parseInt(targetFamilyString, 10) === -1 ? primaryFamilyId : parseInt(targetFamilyString, 10);

            // Handle rejection and merge actions (only for non-primary families)
            if (actionType === PotentialDuplicateActionConstants.REJECT && i > 0) {
                await this.familiesRepository.rejectFamily(currentFamilyId, {});
            } else if (actionType === PotentialDuplicateActionConstants.MERGE && i > 0) {
                await this.familiesRepository.rejectFamily(currentFamilyId, { merge_to_family_id: targetFamilyId });
            }
        }

        // Step 2: Process link actions in parallel
        const linkPromises: Array<Promise<any>> = [];
        for (const [, familyEntry] of this.currentFamilyEntries.entries()) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            const [actionType, targetFamilyString] = selectedAction.value.split('_');
            const targetFamilyId = parseInt(targetFamilyString, 10) === -1 ? primaryFamilyId : parseInt(targetFamilyString, 10);

            if (actionType === PotentialDuplicateActionConstants.LINK) {
                linkPromises.push(
                    this.familiesRepository.linkFamily(targetFamilyId, {
                        family_id: currentFamilyId === -1 ? primaryFamilyId : currentFamilyId
                    })
                );
            }
        }
        await Promise.all(linkPromises);

        // Step 3: Process all status updates in parallel
        const statusUpdatePromises: Array<Promise<any>> = [];
        for (const [i, familyEntry] of this.currentFamilyEntries.entries()) {
            const currentFamilyId = familyEntry.familyDto.id!;
            const selectedAction = selectedActions[currentFamilyId];
            if (!selectedAction) continue;

            // Update family status
            const originalEntry = this.originalFamilyEntries.find(entry => entry.familyDto.id === currentFamilyId);
            const originalStatusUpdates = originalEntry?.statusUpdates;
            const currentStatusUpdates = familyEntry.statusUpdates;

            if (familyEntry.statusUpdates && familyEntry.statusUpdates.family_id > 0 &&
                originalStatusUpdates && !isEqual(originalStatusUpdates, currentStatusUpdates)) {
                statusUpdatePromises.push(this.updateFamilyStatusIfNeeded(familyEntry, currentFamilyId));
            }

            // Process status changes for child entries
            for (const [, childEntries] of Object.entries(this.currentChildrenGroupedByName)) {
                const currentChildEntry = childEntries[i];
                if (currentChildEntry && this.shouldUpdateChildStatusWithFamilyCheck(currentChildEntry)) {
                    statusUpdatePromises.push(this.updateChildStatusIfNeededWithFamilyCheck(currentChildEntry));
                }
            }
        }
        await Promise.all(statusUpdatePromises);
    }

    // Helper method to check if child status should be updated with family check
    private shouldUpdateChildStatusWithFamilyCheck(childEntry: ChildEntry): boolean {
        if (!childEntry?.childDto.id || !childEntry.statusUpdates ||
            childEntry.statusUpdates.status <= 0 || !childEntry.statusUpdates.child_id ||
            childEntry.statusUpdates.child_id <= 0 || !childEntry.statusUpdates.family_id ||
            childEntry.statusUpdates.family_id <= 0) {
            return false;
        }

        // Check against original
        let originalStatusUpdates: StatusChangeInterface | null | undefined;
        for (const [, entries] of Object.entries(this.originalChildrenGroupedByName)) {
            const originalEntry = entries.find(entry => entry.childDto.id === childEntry.childDto.id);
            if (originalEntry) {
                originalStatusUpdates = originalEntry.statusUpdates;
                break;
            }
        }

        return originalStatusUpdates !== undefined &&
               !isEqual(originalStatusUpdates, childEntry.statusUpdates);
    }

    // Helper method for child status update with additional family_id check
    private async updateChildStatusIfNeededWithFamilyCheck(childEntry: ChildEntry): Promise<void> {
        if (
            childEntry &&
            childEntry.childDto.id &&
            childEntry.statusUpdates &&
            childEntry.statusUpdates.status > 0 &&
            childEntry.statusUpdates.child_id &&
            childEntry.statusUpdates.child_id > 0 &&
            childEntry.statusUpdates.family_id &&
            childEntry.statusUpdates.family_id > 0
        ) {
            const currentChildId = childEntry.childDto.id;

            // Find the original child entry to compare against
            let originalStatusUpdates: StatusChangeInterface | null | undefined;
            for (const [, entries] of Object.entries(this.originalChildrenGroupedByName)) {
                const originalEntry = entries.find(entry => entry.childDto.id === currentChildId);
                if (originalEntry) {
                    originalStatusUpdates = originalEntry.statusUpdates;
                    break;
                }
            }

            const currentStatusUpdates = childEntry.statusUpdates;

            // Only update if the entire statusUpdates object has changed from the original
            if (originalStatusUpdates && !isEqual(originalStatusUpdates, currentStatusUpdates)) {
                await this.statusChangeRepository.changeStatus(childEntry.statusUpdates, true);
                this.statusChangesStore.clear(currentChildId);
            }
        }
    }
}
